import { CellContext, Row, Table } from '@tanstack/react-table';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import Button from '../../../components/Button/Button';
import { ButtonVariants } from '../../../components/Button/Button.types';
import Header from '../../../components/Header/Header';
import Link from '../../../components/Link/Link';
import SummaryBlock from '../../../components/SummaryBlock/SummaryBlock';
import { SummaryBlockVariants } from '../../../components/SummaryBlock/SummaryBlock.types';
import TableCard from '../../../components/Tables/TableCard/TableCard';
import Tag from '../../../components/Tag/Tag';
import { TagTypes } from '../../../components/Tag/Tag.types';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { useAdminSubmissions, useCompleteSubmissions } from '../../../services/submissions/submissions.service';
import {
  AdminSubmission,
  AdminSubmissionSummary,
  SubmissionStatus,
  SubmissionStatusLabels,
} from '../../../types/submission.types';
import styles from './Submissions.module.css';
import { useTranslation } from 'react-i18next';
import TooltipDateLabel from '@/components/DateLabel/TooltipDateLabel';

const TagCell = ({ row }: CellContext<AdminSubmission, unknown>) => {
  const { t } = useTranslation();
  const labelType: 'Pending' | 'Completed' = SubmissionStatusLabels[row.original.status];
  const label = t(`submissionStatusLabels.${row.original.status}`) as string;
  return <Tag label={label} type={TagTypes[labelType]} />;
};

const LinkCell = ({ row }: CellContext<AdminSubmission, unknown>) => {
  const { t } = useTranslation();
  const to =
    row.original.status === SubmissionStatus.PENDING
      ? `/submissions/${row.original.orgId}`
      : `/submissions/${row.original.orgId}/${row.original.id}`;
  return <Link to={to}>{t('common.details')}</Link>;
};

const TooltipDateCell = ({ row }: CellContext<AdminSubmission, unknown>) => (
  <TooltipDateLabel date={row.original.lastChangeDate} />
);

const summarizeSubmissions = (data: AdminSubmission[]) =>
  data.reduce<AdminSubmissionSummary>(
    (previousValue: AdminSubmissionSummary, currentValue: AdminSubmission) => {
      const summaries = { ...previousValue };
      if (currentValue.status === SubmissionStatus.PENDING) {
        summaries.pendingCount += 1;
      } else {
        summaries.completedCount += 1;
      }
      return summaries;
    },
    {
      pendingCount: 0,
      completedCount: 0,
    },
  );

const Submissions: FC = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useAdminSubmissions(true);
  const [tableApi, setTableApi] = useState<Table<AdminSubmission> | null>(null);
  const submissions = data?.data || [];
  const { pendingCount, completedCount } = summarizeSubmissions(submissions);
  const { mutate, isLoading: completeIsLoading } = useCompleteSubmissions();

  const markComplete = () => {
    const selectedRows = tableApi?.getSelectedRowModel();
    if (selectedRows?.rows)
      mutate(
        selectedRows.rows.map((row) => row.original.orgId),
        {
          onSuccess: () => {
            tableApi?.resetRowSelection();
            showToast({
              toastType: ToastTypes.Success,
              label: t('submissions.extractsMarkedComplete'),
              id: 'completeSubmissionSuccess',
            });
          },
          onError: (error) => {
            showToast({
              toastType: ToastTypes.Error,
              label: (error as AxiosError).message || t('error.markingSubmissionsComplete'),
              id: 'completeSubmissionError',
            });
          },
        },
      );
  };

  const accessTableApi = (apiParam: Table<AdminSubmission>) => {
    setTableApi(apiParam);
  };

  const getOrgIdName = (orgId: string, fzCode: string, nationalProgramName: string, nationalProgramType: string) => {
    if (orgId.includes('ALL_') || nationalProgramName) {
      const prefix: string = nationalProgramType?.replaceAll('_', ' ');
      return `ALL - ${prefix} (${nationalProgramName})`;
    }
    return fzCode;
  };

  const getStoresCount = (storesAffectedCount: number, nationalProgramId: string, status: string) => {
    if (nationalProgramId && status === 'COMPLETED') {
      return 'ALL';
    }
    return storesAffectedCount;
  };

  return (
    <>
      <Header title={t('extractQueue.title')} />
      <div className={styles.summaryBlocks} data-testid="summaryBlocks">
        <SummaryBlock
          variant={SummaryBlockVariants.LightBlue}
          blocks={[{ count: pendingCount }]}
          title={t('extractQueue.pendingLabel')}
        />
        <SummaryBlock
          variant={SummaryBlockVariants.Green}
          blocks={[{ count: completedCount }]}
          title={t('extractQueue.completedLabel')}
        />
      </div>
      <div className={styles.table}>
        <div className={styles.tableLabel}>
          <h2>{t('extractQueue.sortAndFilterHeader')}</h2>
          <Button variant={ButtonVariants.Primary} onClick={markComplete} isLoading={completeIsLoading}>
            {t('extractQueue.markCompleteButton')}
          </Button>
        </div>
        <TableCard
          enableRowSelection={(row: Row<AdminSubmission>) => {
            if (row.original.status === SubmissionStatus.COMPLETED) return false;
            return true;
          }}
          accessTableApi={accessTableApi}
          includeMinWidth={false}
          className={styles.tableCard}
          data={submissions}
          columns={[
            {
              header: t('extractQueue.table.orgId'),
              accessorFn: ({ orgId, fzCode, nationalProgramName, nationalProgramType }) =>
                getOrgIdName(orgId, fzCode, nationalProgramName, nationalProgramType),
              enableSorting: true,
            },
            {
              header: t('extractQueue.table.numStores'),
              accessorFn: ({ storesAffectedCount, nationalProgramId, status }) =>
                getStoresCount(storesAffectedCount, nationalProgramId, status),
              enableSorting: true,
            },
            {
              header: t('extractQueue.table.lastChange'),
              accessorKey: 'lastChangeDate',
              cell: TooltipDateCell,
              enableSorting: true,
            },
            {
              header: t('extractQueue.table.completedOn'),
              accessorFn: ({ completedDate }) =>
                completedDate ? DateTime.fromISO(completedDate, { zone: 'UTC' }).setZone('local').toFormat('D') : '-',
              enableSorting: true,
            },
            {
              header: t('extractQueue.table.status'),
              accessorKey: 'status',
              cell: TagCell,
              enableSorting: true,
            },
            {
              header: '',
              accessorKey: 'id',
              cell: LinkCell,
            },
          ]}
          isLoading={isLoading}
          returnedRecordsText={(count) => t('submissions.table.returnedRecord', { count })}
        />
      </div>
    </>
  );
};

export default Submissions;
